import { Badge } from "@components/common/badge"
import { ColumnDef } from "@tanstack/react-table"
import { DataTableColumnHeader } from "@components/common/data-table/data-table-column-header"
import { Eye, GripVertical } from "lucide-react"
import { format } from "date-fns"
import { DictType } from "@pma/types"
import { Link } from "react-router-dom"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import ProjectEditor from "./project-editor"
import StatusEditor from "./project-status-editor"
import DeleteProject from "./delete-project"
import DateEditor from "./date-editor"
import useTranslator from "@hooks/use-translator"
import ProjectDetailMenu from "./project-detail-menu"
import { Button } from "../common/button"
import { cn } from "@pma/utils/helpers"

export const projectsColumn: ColumnDef<any>[] = [
  {
    id: "drag-handle",
    header: () => <div className='w-[10px]'></div>,
    cell: ({ row }) => (
      <div className='flex w-[30px] items-center justify-center'>
        <GripVertical
          size={14}
          className='cursor-grab text-muted-foreground focus:outline-none focus:ring-0'
        />
      </div>
    ),
    enableSorting: false,
    size: 30,
  },
  {
    accessorKey: "title",
    header: ({ column }) => <DataTableColumnHeader column={column} title='Name' className='' />,
    cell: ({ row }) => {
      const title = row.getValue("title") as string
      return (
        <div className='items-center'>
          <ProjectEditor
            id={row.original?.id}
            title={"title"}
            value={row.original?.title}
            root_folder={row.original?.root_folder}
          >
            <small className='table-cell truncate text-nowrap'>{title}</small>
          </ProjectEditor>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Work Type' className='hidden md:block' />
    ),
    cell: ({ row }) => {
      const { locale } = useTranslator()
      const isHebrew = locale === "he"
      const type = row.getValue("type") as DictType
      const labelEn = type?.project_type_en
      const labelHe = type?.project_type
      const isBase = labelEn === "construction"
      return (
        <div
          className='hidden items-center truncate text-ellipsis md:flex'
          onClick={(e) => e.stopPropagation()}
        >
          <Badge variant={isBase ? "premium" : "secondary"} className='h-6 min-w-[70px] capitalize'>
            {isHebrew ? labelHe : labelEn}
          </Badge>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "project_number",
    header: ({ column }) => <DataTableColumnHeader column={column} title='Project Number' />,
    cell: ({ row }) => {
      const projectNumber: string = row.getValue("project_number")
      return (
        <div className='flex space-x-2'>
          <ProjectEditor
            title={"project_number"}
            id={row?.original?.id}
            value={row?.original?.project_number}
          >
            <Badge
              variant={"outline"}
              className='table-cell max-w-[500px] truncate font-medium capitalize'
            >
              {projectNumber}
            </Badge>
          </ProjectEditor>
        </div>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "owner",
    header: ({ column }) => <DataTableColumnHeader column={column} title='Project Owner' />,
    cell: ({ row }: DictType) => {
      const ownerDetails = row.getValue("owner")
      return (
        <small className='hidden max-w-[15ch] flex-col justify-start text-wrap md:flex'>
          {ownerDetails?.name}
        </small>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Status' className='hidden md:block' />
    ),
    cell: ({ row }) => {
      const status = row.getValue("status") as DictType
      return <StatusEditor status={status} projectId={row.original.id} />
    },
    enableSorting: false,
  },
  {
    accessorKey: "address_text",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Address' className='hidden md:block' />
    ),
    cell: ({ row }: DictType) => {
      const address = row.getValue("address_text")
      return (
        <small className='hidden max-w-[15ch] flex-col justify-start text-wrap md:flex'>
          {address}
        </small>
      )
    },
    enableSorting: false,
  },
  {
    accessorKey: "start_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title='Duration' className='hidden md:block' />
    ),
    cell: ({ row }: DictType) => {
      const isValidStartTime = row.getValue("start_at") > 0
      const startAt = isValidStartTime ? format(row.getValue("start_at"), "dd MMM yyyy") : "N/A"
      return (
        <DateEditor
          value={{
            from: row.original?.start_at,
            to: row.original?.finish_to,
          }}
          projectId={row?.original?.id}
        />
      )
    },
    enableSorting: false,
  },
  {
    id: "actions",
    header: ({ column }) => <DataTableColumnHeader column={column} title='Actions' />,
    cell: ({ row }) => (
      <div className='flex items-center gap-2'>
        <Link to={PROJECTS_PAGE_ROUTE + `/${row.original.id}`}>
          <Button size={"icon"} variant={"link"}>
            <Eye size={14} />
          </Button>{" "}
        </Link>
        <ProjectDetailMenu project={row?.original} styled={false} />
      </div>
    ),
  },
]
